/**
 * proConnectHost — where Cloud Pro Connect lives in the one web app. Marketplace S3.
 *
 * Cloud Pro Connect is not an industry mode (src/utils/industryMode.js stays
 * as it is): it is a handful of public pages for pros that the same build
 * serves on every host. On cloudproconnect.com "/" is the landing page; on
 * every other host "/" keeps going to the sign-in page, and the landing is
 * reachable at /pro-connect (so it also works in local dev without a hosts
 * alias).
 *
 * The paths are hyphenated on purpose. A tenant's CompanyUrl is its company
 * name with the whitespace removed, so a bare word ("/find", "/join",
 * "/pros") would shadow the admin URLs of a company with that name. Only
 * these three exact paths are static routes; "/pro-connect/<anything else>"
 * still falls through to the tenant routes.
 *
 * Pure module with no imports, so api/ node tests can load it.
 */

export const PRO_CONNECT_HOST_MARK = "cloudproconnect";

export const PRO_CONNECT_PATHS = Object.freeze({
  landing: "/pro-connect",
  find: "/pro-connect/find",
  join: "/pro-connect/join",
  signIn: "/auth/login",
});

function currentHostname() {
  try {
    return typeof window !== "undefined" && window.location ? window.location.hostname || "" : "";
  } catch {
    return "";
  }
}

/**
 * True on cloudproconnect.com and any subdomain or local alias of it
 * (www.cloudproconnect.com, cloudproconnect.local). Substring match, the same
 * style as HOST_MODE in utils/industryMode.js.
 */
export function isProConnectHost(hostname = currentHostname()) {
  return String(hostname || "").toLowerCase().includes(PRO_CONNECT_HOST_MARK);
}

/** Where "home" is for a Pro Connect page: "/" on the Pro Connect host, /pro-connect elsewhere. */
export function landingPath(hostname = currentHostname()) {
  return isProConnectHost(hostname) ? "/" : PRO_CONNECT_PATHS.landing;
}

/**
 * Append the current query string to an in-app path so ?ref= and utm_* survive
 * every hop (landing -> find -> claim, landing -> join). `search` is
 * location.search: "" or "?…".
 */
export function withSearch(path, search = "") {
  const s = String(search || "");
  if (!s || s === "?") return path;
  return `${path}${s.startsWith("?") ? s : `?${s}`}`;
}

/** The claim page for a listing, on this origin, carrying the query string. */
export function claimPathFor(slug, search = "") {
  return withSearch(`/claim/${encodeURIComponent(String(slug || ""))}`, search);
}
